/**
 * Hunter Monitor — Super Admin Tab
 * Full visibility into the Realty Hunter microservice:
 * job queue, proxy health, discovery stats, tenant credit management.
 */

import React, { useState, useEffect } from 'react';
import {
  Target, Activity, Cpu, Shield, Database, Clock,
  Loader2, RefreshCw, CheckCircle, XCircle, AlertCircle,
  TrendingUp, Users, Phone, Plus, Minus,
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '';

const adminFetch = async (url, opts = {}) => {
  const res = await fetch(API_BASE + url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...opts.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
};

// ─── Stat Card ───────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, color = 'text-white', bg = 'bg-white/5' }) => (
  <div className={`p-5 rounded-2xl border border-white/10 ${bg}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-gray-400 text-xs font-medium">{label}</span>
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <p className={`text-3xl font-black ${color}`}>{value ?? '…'}</p>
  </div>
);

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    running:    { cls: 'bg-green-500/20 text-green-400 border-green-500/20',  dot: 'bg-green-400', label: 'Running' },
    paused:     { cls: 'bg-amber-500/20 text-amber-400 border-amber-500/20',  dot: 'bg-amber-400', label: 'Paused'  },
    stopped:    { cls: 'bg-red-500/20 text-red-400 border-red-500/20',         dot: 'bg-red-400',  label: 'Stopped' },
    connecting: { cls: 'bg-blue-500/20 text-blue-400 border-blue-500/20',    dot: 'bg-blue-400', label: 'Connecting' },
  };
  const s = map[status] || map.stopped;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${s.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN HUNTER MONITOR COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function HunterMonitor () {
  const [stats,         setStats        ] = useState(null);
  const [discoveries,   setDiscoveries  ] = useState([]);
  const [tenantCredits, setTenantCredits] = useState([]);
  const [loading,       setLoading      ] = useState(true);
  const [activeTab,     setActiveTab    ] = useState('overview');
  const [adjusting,     setAdjusting    ] = useState({}); // tenantId → loading

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, d, tc] = await Promise.allSettled([
        adminFetch('/api/admin/hunter/stats'),
        adminFetch('/api/admin/hunter/discoveries?limit=50'),
        adminFetch('/api/admin/hunter/tenant-credits'),
      ]);
      if (s.status === 'fulfilled') setStats(s.value);
      if (d.status === 'fulfilled') setDiscoveries(Array.isArray(d.value) ? d.value : (d.value.discoveries || []));
      if (tc.status === 'fulfilled') setTenantCredits(Array.isArray(tc.value) ? tc.value : (tc.value.tenants || []));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const adjustCredits = async (tenantId, amount) => {
    setAdjusting(p => ({ ...p, [tenantId]: true }));
    try {
      await adminFetch('/api/admin/hunter/credits/adjust', {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId, amount }),
      });
      await fetchAll();
    } catch (err) {
      alert(err.message);
    } finally {
      setAdjusting(p => ({ ...p, [tenantId]: false }));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gold-500/10 border border-gold-500/20
          flex items-center justify-center">
          <Target className="w-6 h-6 text-gold-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white">Realty Hunter — Control Center</h2>
          <p className="text-gray-500 text-sm">Monitor crawl jobs, proxy health, and tenant credits</p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl
            bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400
            hover:text-white text-sm transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-navy-800/60 rounded-xl mb-6 w-fit">
        {[
          { id: 'overview',    label: '📊 Overview' },
          { id: 'discoveries', label: '🔍 Discoveries' },
          { id: 'credits',     label: '🪙 Tenant Credits' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all
              ${activeTab === tab.id ? 'bg-gold-500 text-navy-900' : 'text-gray-400 hover:text-white'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gold-400" />
        </div>
      )}

      {/* ── OVERVIEW TAB ── */}
      {!loading && activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPI Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Database}   label="Total Discoveries"   value={(stats?.total_discoveries ?? 0).toLocaleString()} color="text-gold-400"  />
            <StatCard icon={CheckCircle} label="Successful Jobs"    value={(stats?.successful_jobs    ?? 0).toLocaleString()} color="text-green-400" />
            <StatCard icon={XCircle}     label="Blocked / Failed"   value={(stats?.failed_jobs        ?? 0).toLocaleString()} color="text-red-400"   />
            <StatCard icon={Clock}       label="In Queue"            value={(stats?.queued_jobs        ?? 0).toLocaleString()} color="text-blue-400"  />
          </div>

          {/* Service Health */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white font-medium text-sm">BullMQ Queue</span>
                <StatusBadge status={stats?.queue_status ?? 'stopped'} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Workers active</span>
                  <span className="text-white">{stats?.active_workers ?? 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Avg processing time</span>
                  <span className="text-white">{stats?.avg_process_time ?? '—'}</span>
                </div>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white font-medium text-sm">Proxy Health</span>
                <StatusBadge status={stats?.proxy_status ?? 'stopped'} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Active proxies</span>
                  <span className="text-white">
                    {stats?.active_proxies ?? 0} / {stats?.total_proxies ?? 0}
                  </span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-1.5 mt-1">
                  <div
                    className="bg-green-400 h-1.5 rounded-full transition-all"
                    style={{ width: `${stats?.total_proxies ? (stats.active_proxies / stats.total_proxies) * 100 : 0}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white font-medium text-sm">Credit Activity</span>
                <TrendingUp className="w-4 h-4 text-gold-400" />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Total credits sold</span>
                  <span className="text-white">{(stats?.total_credits_sold ?? 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Credits in use</span>
                  <span className="text-white">{(stats?.total_credits_used ?? 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DISCOVERIES TAB ── */}
      {!loading && activeTab === 'discoveries' && (
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-medium">Recent Discoveries</h3>
            <span className="text-gray-500 text-xs">{discoveries.length} records</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  {['Platform', 'Discovered Name', 'Phone', 'Confidence', 'Tenant', 'Date'].map(h => (
                    <th key={h} className="text-left py-3 px-5 text-gray-500 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {discoveries.map((d) => (
                  <tr key={d.id} className="hover:bg-white/3 transition-colors">
                    <td className="py-3 px-5 text-gray-400 text-xs capitalize">{d.platform || '—'}</td>
                    <td className="py-3 px-5 text-white">{d.owner_name || '—'}</td>
                    <td className="py-3 px-5 font-mono text-green-400">{d.phone_number || '—'}</td>
                    <td className="py-3 px-5">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        (d.confidence || 0) >= 0.8 ? 'bg-green-500/20 text-green-400'
                        : (d.confidence || 0) >= 0.5 ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-red-500/20 text-red-400'
                      }`}>
                        {Math.round((d.confidence || 0) * 100)}%
                      </span>
                    </td>
                    <td className="py-3 px-5 text-gray-400 text-xs">{d.tenant_name || `#${d.tenant_id}`}</td>
                    <td className="py-3 px-5 text-gray-500 text-xs">
                      {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
                {discoveries.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-gray-600">
                      No discoveries yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TENANT CREDITS TAB ── */}
      {!loading && activeTab === 'credits' && (
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-medium">Tenant Credit Balances</h3>
            <span className="text-gray-500 text-xs">{tenantCredits.length} tenants</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  {['Tenant', 'Email', 'Balance', 'Total Purchased', 'Adjust'].map(h => (
                    <th key={h} className="text-left py-3 px-5 text-gray-500 font-medium text-xs">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {tenantCredits.map((t) => (
                  <tr key={t.tenant_id} className="hover:bg-white/3 transition-colors">
                    <td className="py-3 px-5 text-white font-medium">{t.name}</td>
                    <td className="py-3 px-5 text-gray-400 text-xs">{t.email}</td>
                    <td className="py-3 px-5">
                      <span className={`font-bold ${t.balance < 5 ? 'text-red-400' : 'text-white'}`}>
                        {t.balance}
                      </span>
                    </td>
                    <td className="py-3 px-5 text-gray-400">{t.total_purchased || 0}</td>
                    <td className="py-3 px-5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => adjustCredits(t.tenant_id, -10)}
                          disabled={adjusting[t.tenant_id]}
                          className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30
                            text-red-400 transition-all disabled:opacity-50"
                          title="Remove 10 credits"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        {adjusting[t.tenant_id] && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />}
                        <button
                          onClick={() => adjustCredits(t.tenant_id, 10)}
                          disabled={adjusting[t.tenant_id]}
                          className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30
                            text-green-400 transition-all disabled:opacity-50"
                          title="Add 10 credits"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {tenantCredits.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-gray-600">
                      No tenant credit data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
